import React, { useState, useEffect, useRef, Suspense, lazy } from "react";
import { dataSlider } from "../../data/Slider/dataSlider";
import "./Slider.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faHandPointUp } from "@fortawesome/free-solid-svg-icons/faHandPointUp";

// Imágenes optimizadas locales de la primera diapositiva (LCP)
import bannerMonografiaWebp from "../../assets/banner_monografia.webp";
import bannerMonografiaAvif from "../../assets/banner_monografia.avif";

// Modal diferido cargado bajo demanda
const SlidersModal = lazy(() => import("./SlidersModal"));

/* Convierte URL relativa a URL absoluta del servidor legacy */
const toAbsUrl = (imgUrl) => {
  if (!imgUrl) return "";
  if (imgUrl.startsWith("http")) return imgUrl;
  return `https://itetlax.org.mx/assets/img/imgC_I/${imgUrl}`;
};

/* dataSlider normalizado (URLs absolutas). Se calcula una sola vez fuera del componente */
const LOCAL_SLIDES = dataSlider.map((item, idx) => ({
  ...item,
  imgUrl: toAbsUrl(item.imgUrl),
  isFirst: idx === 0,
}));

const Slider = () => {
  const slides = LOCAL_SLIDES;
  const [showModal, setShowModal] = useState(false);
  const [currentImage, setCurrentImage] = useState(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [showAllSlides, setShowAllSlides] = useState(false);
  const carouselRef = useRef(null);

  /*
   * activeIndex:
   *   - Datos locales: el item con itemActive === "active", o 0 si ninguno
   */
  const activeIndex = Math.max(
    slides.findIndex((s) => s.itemActive === "active"),
    0
  );

  /* ── Carga diferida de diapositivas secundarias (post-LCP) ── */
  useEffect(() => {
    let idleId = null;
    let timeoutId = null;

    const revealRemainingSlides = () => {
      setShowAllSlides(true);
    };

    if (typeof window !== "undefined") {
      if ("requestIdleCallback" in window) {
        idleId = window.requestIdleCallback(revealRemainingSlides, {
          timeout: 2000,
        });
      } else {
        timeoutId = setTimeout(revealRemainingSlides, 800);
      }
    }

    return () => {
      if (idleId && "cancelIdleCallback" in window) {
        window.cancelIdleCallback(idleId);
      }
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, []);

  const carouselInstanceRef = useRef(null);

  /* ── Precarga el módulo de Carousel tras load o idle para disponibilidad inmediata ── */
  useEffect(() => {
    const preload = () => {
      import(/* webpackChunkName: "bootstrap-carousel" */ "bootstrap/js/dist/carousel").catch(() => {});
    };
    if (typeof window !== "undefined") {
      if (document.readyState === "complete") {
        preload();
      } else {
        window.addEventListener("load", preload, { once: true, passive: true });
      }
    }
  }, []);

  /* ── Inicializa Bootstrap Carousel SOLO cuando todas las diapositivas estén cargadas ── */
  useEffect(() => {
    const el = carouselRef.current;
    if (!el || slides.length === 0 || !showAllSlides) return;

    let isDisposed = false;

    const timer = setTimeout(() => {
      import(/* webpackChunkName: "bootstrap-carousel" */ "bootstrap/js/dist/carousel")
        .then((module) => {
          if (isDisposed || !carouselRef.current) return;
          const Carousel = module.default || module;
          const prev = Carousel.getInstance(el);
          if (prev) prev.dispose();

          const inst = new Carousel(el, {
            interval: 4000,
            ride: "carousel",
            wrap: true,
          });
          carouselInstanceRef.current = inst;
        })
        .catch((err) => console.warn("Bootstrap carousel deferred load failed", err));
    }, 100);

    return () => {
      isDisposed = true;
      clearTimeout(timer);
      if (carouselInstanceRef.current) {
        carouselInstanceRef.current.dispose();
        carouselInstanceRef.current = null;
      }
    };
  }, [showAllSlides, slides]);

  /* Fallbacks de navegación garantizada (funcionan con o sin el script de Bootstrap cargado) */
  const handlePrevSlide = (e) => {
    if (e) e.preventDefault();
    if (carouselInstanceRef.current) {
      carouselInstanceRef.current.prev();
    } else {
      const el = carouselRef.current;
      if (!el) return;
      const items = Array.from(el.querySelectorAll(".carousel-item"));
      const currIdx = items.findIndex((it) => it.classList.contains("active"));
      if (currIdx !== -1) {
        items[currIdx].classList.remove("active");
        const prevIdx = (currIdx - 1 + items.length) % items.length;
        items[prevIdx].classList.add("active");
      }
    }
  };

  const handleNextSlide = (e) => {
    if (e) e.preventDefault();
    if (carouselInstanceRef.current) {
      carouselInstanceRef.current.next();
    } else {
      const el = carouselRef.current;
      if (!el) return;
      const items = Array.from(el.querySelectorAll(".carousel-item"));
      const currIdx = items.findIndex((it) => it.classList.contains("active"));
      if (currIdx !== -1) {
        items[currIdx].classList.remove("active");
        const nextIdx = (currIdx + 1) % items.length;
        items[nextIdx].classList.add("active");
      }
    }
  };

  /* ── Handlers ────────────────────────────────────────────────────────── */
  const handleImageClick = (item) => {
    if (item.modalimage || item.modaltext || item.modalzip) {
      setCurrentImage(item);
      setShowModal(true);
      document.body.style.overflow = "hidden";
    } else if (item.url) {
      window.open(item.url, "_blank");
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setCurrentImage(null);
    document.body.style.overflow = "auto";
    setIsDownloading(false);
  };

  const downloadZipFile = () => {
    if (!currentImage?.zipFile) return;
    setIsDownloading(true);
    const zipUrl = `https://itetlax.org.mx/assets/img/ZIP/${currentImage.zipFile}`;
    setTimeout(() => {
      window.open(zipUrl, "_blank");
      setIsDownloading(false);
    }, 1500);
  };

  /* Renderizar solo la primera diapositiva de inmediato, y las demás al activarse showAllSlides */
  const visibleSlides = showAllSlides ? slides : [slides[activeIndex]];

  /* ── Render ──────────────────────────────────────────────────────────── */
  return (
    <div className="slider-container">
      <div
        id="imageSlider"
        className="carousel slide"
        data-bs-ride={showAllSlides ? "carousel" : "false"}
        ref={carouselRef}
      >
        <div className="carousel-inner">
          {visibleSlides.map((item, index) => {
            const isInitialSlide = index === 0 && !showAllSlides ? true : index === activeIndex;

            return (
              <div
                key={item.imgUrl || `slide-${index}`}
                className={`carousel-item ${index === activeIndex ? "active" : ""}`}
              >
                <div
                  className="image-container"
                  onClick={() => handleImageClick(item)}
                  style={{
                    cursor:
                      item.url ||
                      item.modalimage ||
                      item.modaltext ||
                      item.modalzip
                        ? "pointer"
                        : "default",
                  }}
                >
                  {isInitialSlide ? (
                    <picture style={{ display: "contents" }}>
                      <source srcSet={bannerMonografiaAvif} type="image/avif" />
                      <source srcSet={bannerMonografiaWebp} type="image/webp" />
                      <img
                        src={bannerMonografiaWebp}
                        className="d-block slider-image"
                        alt={item.alt || "Convocatoria ITE"}
                        fetchPriority="high"
                        loading="eager"
                        decoding="async"
                      />
                    </picture>
                  ) : (
                    <img
                      src={item.imgUrl}
                      className="d-block slider-image"
                      alt={item.alt || "Banner institucional"}
                      loading="lazy"
                      fetchPriority="low"
                      decoding="async"
                    />
                  )}

                  {/* Botón pulse */}
                  {item.buttonActive && (
                    <button className="pulse-button" aria-label="Abrir enlace del banner">
                      <FontAwesomeIcon icon={faHandPointUp} style={{ fontSize: "24px" }} />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Flechas móviles (solo si hay más de 1 diapositiva montada) */}
        {showAllSlides && (
          <div className="mobile-nav-arrows">
            <div
              className="mobile-nav-arrow"
              onClick={handlePrevSlide}
              role="button"
              tabIndex={0}
              aria-label="Anterior"
            >
              &lt;
            </div>
            <div
              className="mobile-nav-arrow"
              onClick={handleNextSlide}
              role="button"
              tabIndex={0}
              aria-label="Siguiente"
            >
              &gt;
            </div>
          </div>
        )}

        {/* Controles Bootstrap (solo si hay más de 1 diapositiva montada) */}
        {showAllSlides && (
          <>
            <button
              className="carousel-control-prev"
              type="button"
              data-bs-target="#imageSlider"
              data-bs-slide="prev"
              onClick={handlePrevSlide}
              aria-label="Diapositiva anterior"
            >
              <span
                className="carousel-control-prev-icon"
                aria-hidden="true"
              ></span>
              <span className="visually-hidden">Previous</span>
            </button>
            <button
              className="carousel-control-next"
              type="button"
              data-bs-target="#imageSlider"
              data-bs-slide="next"
              onClick={handleNextSlide}
              aria-label="Diapositiva siguiente"
            >
              <span
                className="carousel-control-next-icon"
                aria-hidden="true"
              ></span>
              <span className="visually-hidden">Next</span>
            </button>
          </>
        )}
      </div>

      {showModal && (
        <Suspense fallback={null}>
          <SlidersModal
            showModal={showModal}
            currentImage={currentImage}
            isDownloading={isDownloading}
            closeModal={closeModal}
            downloadZipFile={downloadZipFile}
          />
        </Suspense>
      )}
    </div>
  );
};

export default Slider;